  import React, { useState, useRef, useCallback } from "react";
  import {
  FaFileAlt,
  FaUpload,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaDownload,
  FaPlus,
  FaIdCard,
  FaCar,
  FaClipboardCheck,
  FaShieldAlt,
  FaSpinner,
  } from "react-icons/fa";
  import { MdVerified, MdOutlineDocumentScanner } from "react-icons/md";
  import * as XLSX from "xlsx";
  import toast from "react-hot-toast";

  // ─── Config ───────────────────────────────────────────────────────────────────
  const API_BASE = process.env.REACT_APP_OCR_API_URL || "http://localhost:8000";

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const formatLabel = (key) =>
  key
  .replace(/_/g, " ")
  .replace(/\b\w/g, (c) => c.toUpperCase());

  const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(2)} MB`;
  };

  const getFieldText = (field) => {
  if (!field) return "";
  if (typeof field === "object") return field.text ?? "";
  return String(field);
  };

  const getFieldStatus = (field) => {
  if (!field || typeof field !== "object") return null;
  return field.status ?? null;
  };

  // ─── Field Card ───────────────────────────────────────────────────────────────
  const FieldCard = ({ label, value, status }) => {
  const isEmpty = !value || value.trim() === "";
  const isValid = status === "VALID";
  const isInvalid = status === "INVALID";

  return (
  <div
  style={{
  background: "#fff",
  border: "1px solid #e8f4f7",
  borderRadius: 12,
  padding: "16px 18px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
  transition: "box-shadow 0.2s",
  borderLeft: isValid
  ? "3px solid #10b981"
  : isInvalid
  ? "3px solid #f43f5e"
  : "3px solid #e2e8f0",
  }}
  >
  <span
  style={{
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.8px",
  color: "#8fadb8",
  }}
  >
  {label}
  </span>
  <span
  style={{
  fontSize: 14,
  fontWeight: 600,
  color: isEmpty ? "#b0c4cc" : "#1e293b",
  fontStyle: isEmpty ? "italic" : "normal",
  wordBreak: "break-word",
  fontFamily: isEmpty ? "inherit" : "'JetBrains Mono', monospace",
  }}
  >
  {isEmpty ? "—" : value}
  </span>
  {status && (
  <span
  style={{
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  color: isValid ? "#10b981" : "#f43f5e",
  display: "flex",
  alignItems: "center",
  gap: 4,
  }}
  >
  {isValid ? <FaCheckCircle size={9} /> : <FaTimesCircle size={9} />}
  {status}
  </span>
  )}
  </div>
  );
  };

  // ─── Score Circle ─────────────────────────────────────────────────────────────
  const ScoreCircle = ({ score }) => {
  const r = 42;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color =
  score >= 80 ? "#10b981" : score >= 50 ? "#f59e0b" : "#f43f5e";

  return (
  <div style={{ position: "relative", width: 110, height: 110, flexShrink: 0 }}>
  <svg
  viewBox="0 0 100 100"
  style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}
  >
  <circle
  cx="50" cy="50" r={r}
  fill="none"
  stroke="rgba(43,181,206,0.1)"
  strokeWidth="7"
  />
  <circle
  cx="50" cy="50" r={r}
  fill="none"
  stroke={color}
  strokeWidth="7"
  strokeLinecap="round"
  strokeDasharray={circ}
  strokeDashoffset={offset}
  style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)" }}
  />
  </svg>
  <div
  style={{
  position: "absolute", inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 24, fontWeight: 800, color: "#1e293b" }}>
            {score}%
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#8fadb8", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Match
          </span>
        </div>
      </div>
    );
  };

  const CheckItem = ({ label, passed, score }) => {
    const isInvalid = !passed;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 12px",
          background: "#f8fafc",
          borderRadius: 8,
          border: "1px solid #f1f5f9",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {passed ? (
            <FaCheckCircle color="#10b981" size={14} />
          ) : (
            <FaTimesCircle color="#f43f5e" size={14} />
          )}
          <span style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
            {label}
          </span>
        </div>
        {score !== undefined && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: passed ? "#10b981" : "#f43f5e",
            }}
          >
            {score}% Match
          </span>
        )}
      </div>
    );
  };

  const VerifyCard = ({ label, data, isBoolean }) => {
    const passed = isBoolean ? data : data?.match;
    const score = data?.score;
    const statusColor = passed ? "#10b981" : "#f43f5e";

    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #e8f4f7",
          borderRadius: 12,
          padding: "14px 16px",
          boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#8fadb8",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: 8,
          }}
        >
          {label}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {passed ? (
            <FaCheckCircle color="#10b981" size={16} />
          ) : (
            <FaTimesCircle color="#f43f5e" size={16} />
          )}
          <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
            {passed ? "MATCHED" : "MISMATCH"}
          </span>
          {!isBoolean && score !== undefined && (
            <span
              style={{
                marginLeft: "auto",
                fontSize: 12,
                fontWeight: 700,
                color: statusColor,
                background: statusColor + "15",
                padding: "2px 8px",
                borderRadius: 12,
              }}
            >
              {score}% Match
            </span>
          )}
        </div>
      </div>
    );
  };

  const ProcessingView = ({ currentStep }) => {
    const STEPS = [
      "Reading PDF text layer...",
      "Extracting form fields...",
      "Reconciling name profiles...",
      "Verifying details with Aadhaar & Driving Licence..."
    ];

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
        }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            position: "relative",
            marginBottom: 24,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                position: "absolute",

  inset: i * 12,
  borderRadius: "50%",
  border: "3px solid transparent",
  borderTopColor: ["#2BB5CE", "#1a9db8", "#6366f1"][i],
  animation: `spin ${[1.8, 1.3, 1][i]}s linear infinite ${i % 2 === 1 ? "reverse" : ""}`,
  }}
  />
  ))}
  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>

  <h3 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", marginBottom: 24 }}>
  Analyzing Document
  </h3>
  <div style={{ display: "flex", flexDirection: "column", gap: 12, textAlign: "left" }}>
      {STEPS.map((step, i) => {
        const done = i < currentStep;
        const active = i === currentStep;

        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontSize: 14,
              color: done ? "#10b981" : active ? "#1e293b" : "#94a3b8",
              fontWeight: active ? 600 : 400,
              transition: "all 0.3s",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                flexShrink: 0,
                background: done ? "#10b981" : active ? "#2BB5CE" : "#e2e8f0",
                boxShadow: active ? "0 0 10px rgba(43,181,206,0.5)" : "none",
                animation: active ? "pulse-dot 1.5s infinite" : "none",
              }}
            />
            {done && (
              <FaCheckCircle
                size={11}
                style={{ color: "#10b981", marginLeft: -2 }}
              />
            )}
            {step}
          </div>
        );
      })}
    </div>
  </div>
);
};
 
  // ─── Main styles object ───────────────────────────────────────────────────────
  const styles = {
  page: {
  padding: "28px 28px",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  background: "#f4fbfc",
  minHeight: "100vh",
  },
  pageHeader: {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 16,
  marginBottom: 28,
  },
  pageTitle: {
  fontSize: 24,
  fontWeight: 800,
  color: "#0f172a",
  letterSpacing: "-0.5px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  },
  pageSub: { fontSize: 13, color: "#8fadb8", marginTop: 4 },
  card: {
  background: "#fff",
  border: "1px solid #e8f4f7",
  borderRadius: 16,
  padding: "24px 28px",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  marginBottom: 20,
  },
  uploadZone: {
  border: "2px dashed #b2dce8",
  borderRadius: 14,
  padding: "40px 24px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 14,
  cursor: "pointer",
  transition: "all 0.2s ease",
  background: "#f8fdfe",
  textAlign: "center",
  },
  uploadZoneActive: {
  borderColor: "#2BB5CE",
  background: "rgba(43,181,206,0.05)",
  boxShadow: "0 0 24px rgba(43,181,206,0.15)",
  },
  btnPrimary: {
  display: "flex", alignItems: "center", gap: 8,
  padding: "13px 28px",
  background: "linear-gradient(135deg, #2BB5CE 0%, #1a9db8 100%)",
  border: "none", borderRadius: 10,
  color: "#fff", fontSize: 14, fontWeight: 700,
  cursor: "pointer", fontFamily: "inherit",
  boxShadow: "0 4px 16px rgba(43,181,206,0.35)",
  transition: "all 0.2s",
  width: "100%",
  justifyContent: "center",
  },
  btnDisabled: {
  opacity: 0.4,
  cursor: "not-allowed",
  boxShadow: "none",
  },
  btnSecondary: {
  display: "flex", alignItems: "center", gap: 8,
  padding: "10px 18px",
  background: "#fff", border: "1px solid #d1e8ef",
  borderRadius: 9, color: "#1e293b", fontSize: 13,
  fontWeight: 600, cursor: "pointer",
  fontFamily: "inherit", transition: "all 0.2s",
  },
  btnExport: {
  display: "flex", alignItems: "center", gap: 8,
  padding: "10px 20px",
  background: "linear-gradient(135deg, #059669, #10b981)",
  border: "none", borderRadius: 9,
  color: "#fff", fontSize: 13, fontWeight: 700,
  cursor: "pointer", fontFamily: "inherit",
  boxShadow: "0 4px 14px rgba(16,185,129,0.3)",
  transition: "all 0.2s",
  },
  scoreBanner: {
  background: "#fff",
  border: "1px solid #e8f4f7",
  borderRadius: 16,
  padding: "28px 32px",
  display: "flex",
  alignItems: "center",
  gap: 36,
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  marginBottom: 20,
  flexWrap: "wrap",
  },
  verifyCard: {
  background: "#fff",
  border: "1px solid #e8f4f7",
  borderRadius: 12,
  padding: "18px 20px",
  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
  },
  processingWrap: {
  display: "flex", alignItems: "center",
  justifyContent: "center", minHeight: "60vh",
  },
  processingCard: {
  background: "#fff",
  border: "1px solid #e8f4f7",
  borderRadius: 20,
  padding: "52px 72px",
  textAlign: "center",
  maxWidth: 440,
  width: "100%",
  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
  },
  };

  // ─── Main Component ───────────────────────────────────────────────────────────
  const DocVerification = () => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [phase, setPhase] = useState("upload"); // upload | processing | results
  const [currentStep, setCurrentStep] = useState(0);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("form");
  const fileInputRef = useRef(null);

  // ── File handling ────────────────────────────────────────────────────────────
  const handleFile = (f) => {
  if (!f) return;
  if (!f.name.toLowerCase().endsWith(".pdf")) {
  toast.error("Only PDF files are allowed.");
  return;
  }
  setFile(f);
  };

  const onDrop = useCallback((e) => {
  e.preventDefault();
  setIsDragging(false);
  const f = e.dataTransfer.files?.[0];
  handleFile(f);
  }, []);

  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);

  // ── Upload & verify ──────────────────────────────────────────────────────────
  // ── Upload & verify ──────────────────────────────────────────────────────────
  const handleVerify = async () => {
    if (!file) return;
    setPhase("processing");
    setCurrentStep(0);
    setResult(null);
    setActiveTab("form");

    // Animate steps while waiting
    const stepTimings = [1200, 2800, 5000];
    stepTimings.forEach((delay, i) => {
      setTimeout(() => setCurrentStep(i + 1), delay);
    });

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }

      const data = await res.json();
      setCurrentStep(4);
      setTimeout(() => {
        setResult(data);
        setPhase("results");
      }, 600);
    } catch (err) {
      toast.error(`Verification failed: ${err.message}`);
      setPhase("upload");
      setCurrentStep(0);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPhase("upload");
    setCurrentStep(0);
    setActiveTab("form");
  };

  // ── Excel Export ─────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!result) return;
    try {
      const resData = result?.data ?? result ?? {};
      const form1 = resData?.form?.page1 ?? result?.form?.page1 ?? {};
      const form2 = resData?.form?.page2 ?? result?.form?.page2 ?? {};
      const aadh  = resData?.aadhaar?.data ?? resData?.aadhaar ?? result?.aadhaar ?? {};
      const dl    = resData?.driving_licence?.data ?? resData?.driving_licence ?? result?.driving_licence ?? {};
      const ver   = resData?.verification ?? result?.verification ?? {};

      const row = {
        "File Name": file?.name ?? "—",
        "Pages": result.pages ?? "—",
        "Verification Score": ver.score ?? "—",
        "Status": ver.status ?? "—",
        "Timestamp": new Date().toLocaleString(),
        // Form Page 1
        "Candidate Name":           getFieldText(form1.candidate_name),
        "Father Name":              getFieldText(form1.father_name),
        "Mother Name":              getFieldText(form1.mother_name),
        "Date of Birth (Form)":     getFieldText(form1.dob),
        "Aadhaar (Form)":           getFieldText(form1.aadhaar),
        "Marital Status":           getFieldText(form1.marital_status),
        "Religion":                 getFieldText(form1.religion),
        "Caste":                    getFieldText(form1.caste),
        "Annual Income":            getFieldText(form1.annual_income),
        "Address":                  getFieldText(form1.address),
        "District":                 getFieldText(form1.district),
        "State":                    getFieldText(form1.state),
        "Pincode":                  getFieldText(form1.pincode),
        "DL Number (Form)":         getFieldText(form1.driving_licence_number),
        "DL Issue Date (Form)":     getFieldText(form1.driving_licence_issue_date),
        "DL Valid Till (Form)":     getFieldText(form1.driving_licence_valid_till),
        // Form Page 2
        "DL Type":                  getFieldText(form2.driving_licence_type),
        "DL Issuing Authority":     getFieldText(form2.dl_issuing_authority),
        "Mobile Number":            getFieldText(form2.mobile_number),
        "Qualification":            getFieldText(form2.qualification),
        "Experience (Years)":       getFieldText(form2.experience_in_years),
        "Employer Name":            getFieldText(form2.present_employer_name),
        "Employer Address":         getFieldText(form2.employer_address),
        "Employer Contact":         getFieldText(form2.employer_contact_number),
        // Aadhaar
        "Aadhaar Name":             getFieldText(aadh.name),
        "Aadhaar DOB":              getFieldText(aadh.date_of_birth || aadh.dob),
        "Aadhaar Gender":           getFieldText(aadh.gender),
        "Aadhaar Number":           getFieldText(aadh.aadhaar_number),
        // DL
        "DL Number":                getFieldText(dl.driving_licence_number),
        "DL Name":                  getFieldText(dl.name),
        "DL Father Name":           getFieldText(dl.father_name),
        "DL DOB":                   getFieldText(dl.date_of_birth),
        "DL Issue Date":            getFieldText(dl.date_of_issue),
        "DL Valid Transport":       getFieldText(dl.valid_till_transport),
        "DL Valid Non-Transport":   getFieldText(dl.valid_till_non_transport),
        "Blood Group":              getFieldText(dl.blood_group),
        "Vehicle Classes":          getFieldText(dl.vehicle_classes),
        "Issuing Authority":        getFieldText(dl.issuing_authority),
        // Verification
        "Name vs Aadhaar Match":    ver.name_form_vs_aadhaar?.match ? "Yes" : "No",
        "Name vs Aadhaar Score":    ver.name_form_vs_aadhaar?.score ?? "—",
        "Name vs DL Match":         ver.name_form_vs_dl?.match ? "Yes" : "No",
        "Name vs DL Score":         ver.name_form_vs_dl?.score ?? "—",
        "DOB Match":                ver.dob_match ? "Yes" : "No",
        "Aadhaar Number Match":     ver.aadhaar_match?.match ? "Yes" : "No",
        "Aadhaar Number Score":     ver.aadhaar_match?.score ?? "—",
        "DL Number Match":          ver.driving_licence_match?.match ? "Yes" : "No",
        "DL Number Score":          ver.driving_licence_match?.score ?? "—",
      };

      const ws = XLSX.utils.json_to_sheet([row]);
      ws["!cols"] = Object.keys(row).map(() => ({ wch: 24 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Verification");
      const timestamp = new Date().toISOString().slice(0, 10);
      const safeFilename = (file?.name ?? "document").replace(/\.pdf$/i, "");
      XLSX.writeFile(wb, `DocVerify_${safeFilename}_${timestamp}.xlsx`);
      toast.success("Excel exported successfully!");
    } catch (err) {
      toast.error("Export failed: " + err.message);
    }
  };

  const renderFieldGrid = (dataObj) => {
    if (!dataObj || Object.keys(dataObj).length === 0) {
      return (
        <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 48, color: "#b0c4cc", fontSize: 14 }}>
          No data extracted for this section.
        </div>
      );
    }
    return Object.entries(dataObj).map(([key, val]) => (
      <FieldCard
        key={key}
        label={formatLabel(key)}
        value={getFieldText(val)}
        status={getFieldStatus(val)}
      />
    ));
  };

  // ─── TABS config ─────────────────────────────────────────────────────────────
  const tabs = [
    { id: "form",         label: "Application Form", icon: <FaFileAlt size={13} /> },
    { id: "aadhaar",      label: "Aadhaar",          icon: <FaIdCard size={13} /> },
    { id: "dl",           label: "Driving Licence",  icon: <FaCar size={13} /> },
    { id: "verification", label: "Verification",     icon: <FaShieldAlt size={13} /> },
  ];

  // ─── UPLOAD PHASE ────────────────────────────────────────────────────────────
  if (phase === "upload") {
  return (
  <div style={styles.page}>
  <div style={styles.pageHeader}>
  <div>
  <h1 style={styles.pageTitle}>
  <MdOutlineDocumentScanner size={26} color="#2BB5CE" />
  Document Verification
  </h1>
  <p style={styles.pageSub}>
  Upload a candidate PDF to extract and cross-verify Aadhaar, Driving Licence &amp; Form data
  </p>
  </div>
  </div>

  <div style={{ maxWidth: 560, margin: "0 auto" }}>
  <div style={styles.card}>
  {/* Upload Zone */}
  <div
  style={{
  ...styles.uploadZone,
  ...(isDragging ? styles.uploadZoneActive : {}),
  }}
  onDrop={onDrop}
  onDragOver={onDragOver}
  onDragLeave={onDragLeave}
  onClick={() => !file && fileInputRef.current?.click()}
  >
  <input
  ref={fileInputRef}
  type="file"
  accept=".pdf"
  hidden
  onChange={(e) => handleFile(e.target.files?.[0])}
  />

  <div
  style={{
  width: 76, height: 76, borderRadius: "50%",
  background: "linear-gradient(135deg, rgba(43,181,206,0.15), rgba(26,157,184,0.1))",
  border: "1px solid rgba(43,181,206,0.3)",
  display: "flex", alignItems: "center", justifyContent: "center",
  animation: "ring-pulse 3s ease-in-out infinite",
  }}
  >
  <style>{`@keyframes ring-pulse{0%,100%{transform:scale(1);opacity:0.8}50%{transform:scale(1.06);opacity:1}}`}</style>
  <FaUpload size={28} color="#2BB5CE" />
  </div>

  <div>
  <p style={{ fontSize: 17, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>
  Drop your PDF here
  </p>
  <p style={{ fontSize: 13, color: "#8fadb8" }}>
  or{" "}
  <button
  style={{
  background: "none", border: "none", cursor: "pointer",
  color: "#2BB5CE", fontWeight: 700, fontSize: 13,
  textDecoration: "underline", textUnderlineOffset: 3,
  fontFamily: "inherit", padding: 0,
  }}
  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
  >
  browse files
  </button>
  </p>
  </div>

  <div
  style={{
  display: "flex", alignItems: "center", gap: 6,
  fontSize: 12, color: "#b0c4cc",
  background: "rgba(43,181,206,0.06)",
  padding: "6px 14px", borderRadius: 20,
  }}
  >
  📄 PDF files only · Candidate form + Aadhaar + Driving Licence
  </div>
  </div>

  {/* File preview */}
  {file && (
  <div
  style={{
  display: "flex", alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 16px", marginTop: 16,
  background: "rgba(43,181,206,0.07)",
  border: "1px solid rgba(43,181,206,0.2)",
  borderRadius: 10,
  animation: "slideIn 0.3s ease",
  }}
  >
  <style>{`@keyframes slideIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
  <div
  style={{
  width: 40, height: 40, borderRadius: 9,
  background: "linear-gradient(135deg, #2BB5CE, #1a9db8)",
  display: "flex", alignItems: "center", justifyContent: "center",
  }}
  >
  <FaFileAlt size={18} color="#fff" />
  </div>
  <div>
  <p style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
  {file.name}
  </p>
  <p style={{ fontSize: 12, color: "#8fadb8", marginTop: 2 }}>
  {formatBytes(file.size)}
  </p>
  </div>
  </div>
  <button
  onClick={() => setFile(null)}
  style={{
  background: "rgba(244,63,94,0.1)",
  border: "1px solid rgba(244,63,94,0.2)",
  borderRadius: 8, color: "#f43f5e",
  width: 32, height: 32, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  }}
  >
  <FaTimesCircle size={14} />
  </button>
  </div>
  )}

  {/* Submit */}
  <button
  style={{
  ...styles.btnPrimary,
  marginTop: 18,
  ...(!file ? styles.btnDisabled : {}),
  }}
  disabled={!file}
  onClick={handleVerify}
  >
  <MdVerified size={18} />
  Verify Document
  </button>
  </div>

  {/* Info cards */}
  <div
  style={{
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 12,
  }}
  >
  {[
  { icon: "📝", label: "Application Form", desc: "Handwritten field OCR" },
  { icon: "🪪", label: "Aadhaar Card",     desc: "ID number & DOB extract" },
  { icon: "🚗", label: "Driving Licence",  desc: "DL data cross-check" },
  ].map((c) => (
  <div
  key={c.label}
  style={{
  background: "#fff", border: "1px solid #e8f4f7",
  borderRadius: 12, padding: "16px 14px", textAlign: "center",
  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
  }}
  >
  <div style={{ fontSize: 24, marginBottom: 8 }}>{c.icon}</div>
  <p style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{c.label}</p>
  <p style={{ fontSize: 11, color: "#8fadb8", marginTop: 4 }}>{c.desc}</p>
  </div>
  ))}
  </div>
  </div>
  </div>
  );
  }

  // ─── PROCESSING PHASE ────────────────────────────────────────────────────────
  if (phase === "processing") {
  return (
  <div style={styles.page}>
  <div style={styles.pageHeader}>
  <h1 style={styles.pageTitle}>
  <MdOutlineDocumentScanner size={26} color="#2BB5CE" />
  Document Verification
  </h1>
  </div>
  <ProcessingView currentStep={currentStep} />
  </div>
  );
  }

  // ─── RESULTS PHASE ───────────────────────────────────────────────────────────
  const resData = result?.data ?? result ?? {};
  const form1 = resData?.form?.page1 ?? result?.form?.page1 ?? {};
  const form2 = resData?.form?.page2 ?? result?.form?.page2 ?? {};
  const aadh  = resData?.aadhaar?.data ?? resData?.aadhaar ?? result?.aadhaar ?? {};
  const dl    = resData?.driving_licence?.data ?? resData?.driving_licence ?? result?.driving_licence ?? {};
  const ver   = resData?.verification ?? result?.verification ?? {};

  const score  = ver.score ?? 0;
  const status = ver.status ?? "—";
  const statusColor =
  status === "VERIFIED" ? "#10b981" :
  status === "REVIEW"   ? "#f59e0b" : "#f43f5e";

  return (
  <div style={styles.page}>
  {/* Header */}
  <div style={styles.pageHeader}>
  <div>
  <h1 style={styles.pageTitle}>
  <MdOutlineDocumentScanner size={26} color="#2BB5CE" />
  Verification Report
  </h1>
  <p style={styles.pageSub}>
  {file?.name} &nbsp;·&nbsp; {result?.page_count ?? result?.pages ?? "—"} pages extracted
  </p>
  </div>
  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
  <button style={styles.btnSecondary} onClick={handleReset}>
  <FaPlus size={12} /> New Upload
  </button>
  <button style={styles.btnExport} onClick={handleExport}>
  <FaDownload size={12} /> Export Excel
  </button>
  </div>
  </div>

  {/* Score Banner */}
  <div style={styles.scoreBanner}>
  <div style={{ display: "flex", alignItems: "center", gap: 24, flexShrink: 0 }}>
  <ScoreCircle score={score} />
  <div>
  <div
  style={{
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "5px 16px", borderRadius: 20,
  fontSize: 13, fontWeight: 800,
  textTransform: "uppercase", letterSpacing: "0.6px",
  background: statusColor + "18",
  color: statusColor,
  border: `1px solid ${statusColor}40`,
  marginBottom: 10,
  }}
  >
  {status === "VERIFIED" ? <FaCheckCircle size={12} /> : <FaExclamationTriangle size={12} />}
  {status}
  </div>
  <p style={{ fontSize: 13, color: "#8fadb8", maxWidth: 180, lineHeight: 1.5 }}>
  {status === "VERIFIED"
  ? "All key fields cross-verified successfully."
  : "Some fields need manual review."}
  </p>
  </div>
  </div>

  {/* Check summary */}
  <div
  style={{
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
  gap: 10,
  flex: 1,
  }}
  >
  <CheckItem
  label="Name vs Aadhaar"
  passed={ver.name_form_vs_aadhaar?.match}
  score={ver.name_form_vs_aadhaar?.score}
  />
  <CheckItem
  label="Name vs DL"
  passed={ver.name_form_vs_dl?.match}
  score={ver.name_form_vs_dl?.score}
  />
  <CheckItem label="Date of Birth" passed={ver.dob_match} />
  <CheckItem
  label="Aadhaar Number"
  passed={ver.aadhaar_match?.match}
  score={ver.aadhaar_match?.score}
  />
  <CheckItem
  label="DL Number"
  passed={ver.driving_licence_match?.match}
  score={ver.driving_licence_match?.score}
  />
  </div>
  </div>

  {/* Tabs */}
  <div
  style={{
  display: "flex", gap: 4, padding: 5,
  background: "rgba(43,181,206,0.06)",
  border: "1px solid #ddf0f4",
  borderRadius: 12, width: "fit-content",
  marginBottom: 20, overflowX: "auto",
  }}
  >
  {tabs.map((t) => (
  <button
  key={t.id}
  onClick={() => setActiveTab(t.id)}
  style={{
  display: "flex", alignItems: "center", gap: 7,
  padding: "8px 16px", border: "none", borderRadius: 9,
  fontSize: 13, fontWeight: 600, cursor: "pointer",
  fontFamily: "inherit", whiteSpace: "nowrap",
  transition: "all 0.2s",
  background:
  activeTab === t.id
  ? "linear-gradient(135deg, #2BB5CE, #1a9db8)"
  : "transparent",
  color: activeTab === t.id ? "#fff" : "#5a7a88",
  boxShadow:
  activeTab === t.id ? "0 2px 10px rgba(43,181,206,0.3)" : "none",
  }}
  >
  {t.icon} {t.label}
  </button>
  ))}
  </div>

  {/* Tab: Form */}
  {activeTab === "form" && (
  <div>
  <p style={{ fontSize: 12, color: "#8fadb8", marginBottom: 14, fontWeight: 600 }}>
  PAGE 1 — Personal Details
  </p>
  <div
  style={{
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 12, marginBottom: 24,
  }}
  >
  {renderFieldGrid(form1)}
  </div>

  <p style={{ fontSize: 12, color: "#8fadb8", marginBottom: 14, fontWeight: 600 }}>
  PAGE 2 — Employment Details
  </p>
  <div
  style={{
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 12,
  }}
  >
  {renderFieldGrid(form2)}
  </div>
  </div>
  )}

  {/* Tab: Aadhaar */}
  {activeTab === "aadhaar" && (
  <div
  style={{
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 12,
  }}
  >
  {renderFieldGrid(aadh)}
  </div>
  )}

  {/* Tab: Driving Licence */}
  {activeTab === "dl" && (
  <div
  style={{
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 12,
  }}
  >
  {renderFieldGrid(dl)}
  </div>
  )}

  {/* Tab: Verification */}
  {activeTab === "verification" && (
  <div
  style={{
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 14,
  }}
  >
  <VerifyCard label="Name — Form vs Aadhaar" data={ver.name_form_vs_aadhaar} />
  <VerifyCard label="Name — Form vs DL"      data={ver.name_form_vs_dl} />
  <VerifyCard label="Aadhaar Number Match"   data={ver.aadhaar_match} />
  <VerifyCard label="DL Number Match"        data={ver.driving_licence_match} />
  <VerifyCard label="Date of Birth Match"    data={ver.dob_match} isBoolean />
  <div style={styles.verifyCard}>
  <div style={{ fontSize: 13, color: "#5a7a88", fontWeight: 500, marginBottom: 14 }}>
  Overall Status
  </div>
  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
  <ScoreCircle score={score} />
  <div>
  <div
  style={{
  fontSize: 13, fontWeight: 700,
  color: statusColor,
  background: statusColor + "18",
  border: `1px solid ${statusColor}40`,
  padding: "4px 14px", borderRadius: 20,
  display: "inline-block", marginBottom: 8,
  }}
  >
  {status}
  </div>
  <p style={{ fontSize: 12, color: "#8fadb8", lineHeight: 1.6 }}>
  Score ≥ 80 → VERIFIED<br />
  Score &lt; 80 → REVIEW
  </p>
  </div>
  </div>
  </div>
  </div>
  )}
  </div>
  );
  };

  export default DocVerification;

